import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Sparkles, Camera, Upload, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AIDetectDialog - Take/upload a photo and detect item details using AI
 * 
 * Props:
 *   open, onClose - dialog state
 *   detectionType - "item" | "prop" | "costume"
 *   onDetected - callback with { name, description, category_suggestion, condition, color, size_type, size_value }
 */
const AIDetectDialog = ({ open, onClose, detectionType, onDetected }) => {
  const { getAuthHeaders } = useAuth();
  const [imageBase64, setImageBase64] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const reset = () => {
    setImageBase64(null);
    setPreviewUrl(null);
    setDetecting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please use JPEG, PNG, or WEBP images only');
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setPreviewUrl(dataUrl);
      // Strip data URL prefix for base64
      const base64 = dataUrl.split(',')[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleDetect = async () => {
    if (!imageBase64) return;
    setDetecting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/detect`,
        {
          image_base64: imageBase64,
          detection_type: detectionType
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Item detected! Details auto-filled below.');
      onDetected(response.data);
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI detection failed. Please try again.');
    } finally {
      setDetecting(false);
    }
  };

  const typeLabel = {
    item: 'AV Equipment',
    prop: 'Prop',
    costume: 'Costume'
  }[detectionType] || 'Item';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="ai-detect-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            AI Detect {typeLabel}
          </DialogTitle>
          <DialogDescription>
            Take or upload a photo and AI will identify the {typeLabel.toLowerCase()} and fill in the details automatically.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {previewUrl ? (
            <div className="relative rounded-xl overflow-hidden border border-border">
              <img src={previewUrl} alt="Preview" className="w-full max-h-64 object-contain bg-muted" />
              <button
                onClick={() => { setPreviewUrl(null); setImageBase64(null); }}
                className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full text-white hover:bg-black/70"
                data-testid="clear-preview-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {/* Camera capture */}
              <button
                data-testid="ai-camera-btn"
                onClick={() => cameraInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <Camera className="w-8 h-8 text-primary" />
                <span className="text-sm font-medium">Take Photo</span>
                <span className="text-xs text-muted-foreground">Use camera</span>
              </button>
              
              {/* File upload */}
              <button
                data-testid="ai-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <Upload className="w-8 h-8 text-primary" />
                <span className="text-sm font-medium">Upload Photo</span>
                <span className="text-xs text-muted-foreground">From device</span>
              </button>
            </div>
          )}
          
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelected}
            className="hidden"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelected}
            className="hidden"
          />

          {previewUrl && !detecting && (
            <div className="text-sm text-muted-foreground text-center">
              Ready to analyze. Click "Detect" to identify the {typeLabel.toLowerCase()}.
            </div>
          )}
          
          {detecting && (
            <div className="flex items-center justify-center gap-2 py-4 text-primary">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="font-medium">AI is analyzing the image...</span>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={detecting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDetect} 
            disabled={!imageBase64 || detecting}
            data-testid="ai-detect-submit-btn"
          >
            {detecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Detecting...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Detect with AI
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIDetectDialog;
